import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  editComplaint,
  editInvestigation,
} from "services/advocateComplaintService";
import { AdvocateComplaints, Investigation } from "types";
import { LoadingIcon } from "utils/constant";
import {
  ADVOCATE_INVESTIGATION_STATUS,
  COMPLIANT_STATUS,
  INVESTIGATION_STATUS,
} from "utils/constantValues";
import { useTranslation } from "contexts/i18nContext";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: AdvocateComplaints;
  investigationData: Investigation;
  onInvestigationRefresh: any;
}

export default function AddChargeDocModal({
  isOpen,
  setIsOpen,
  complaintData,
  investigationData,
  onInvestigationRefresh,
}: ModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [formState, setFormState] = useState({
    charge_summary: "",
    artcicle_of_charge: "",
    status: INVESTIGATION_STATUS?.ON_CHARGE,
    files: [] as File[],
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (investigationData) {
      setFormState((prev) => ({
        ...prev,
        charge_summary: investigationData.charge_summary || "",
        artcicle_of_charge: investigationData.artcicle_of_charge || "",
      }));
    }
  }, [investigationData]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const removeFile = () => {
    setFormState((prev) => ({ ...prev, files: [] }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formState.charge_summary.trim()) {
      showToast(t("provideChargeSummary"), "error");
      return;
    }

    if (!formState.artcicle_of_charge.trim()) {
      showToast(t("provideArticleOfCharge"), "error");
      return;
    }

    if (formState.files.length === 0) {
      showToast(t("uploadChargeDocument"), "error");
      return;
    }

    if (!investigationData?.uu_id) {
      showToast(t("investigationNotFound"), "error");
      return;
    }

    setIsSubmitLoading(true);

    const data = new FormData();
    data.append("charge_documents", formState?.files[0] || "");
    data.append("charge_summary", formState.charge_summary);
    data.append("artcicle_of_charge", formState.artcicle_of_charge);
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );
    data.append("complaint", String(complaintData?.uu_id));
    data.append(
      "status",
      ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_COORDINATOR,
    );
    data.append("is_prosecutor_charge_doc", true.toString());

    const investigation_id = investigationData?.uu_id;
    const isFormData = true;

    try {
      const resData = await editInvestigation(
        data,
        investigation_id,
        isFormData,
      );

      if (resData) {
        await editComplaint({
          ...complaintData,
          status:
            complaintData?.status === COMPLIANT_STATUS.RETURNED
              ? COMPLIANT_STATUS.DRAFT_CHARGE_UPDAED
              : COMPLIANT_STATUS.DRAFT_CHARGE,
        });
      }

      onInvestigationRefresh();
      setIsOpen(false);
      showToast(t("chargeDocumentFramed"), "success");
      setFormState({
        charge_summary: "",
        artcicle_of_charge: "",
        status: INVESTIGATION_STATUS.ON_CHARGE,
        files: [] as File[],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("failedToFrameCharge"), "error");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("frameChargeDocument")}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Case Info */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-800 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {t("framingCharges")}
              </p>
              <p className="text-xs text-gray-600">
                {t("case")}: {complaintData?.complaint_number}
              </p>
            </div>
          </div>
        </div>

        {/* Charge Details */}
        <section className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="text-sm font-semibold text-gray-700">
                  {t("chargeSummary")}
                </h3>
              </div>
            </div>
            <textarea
              required
              rows={4}
              name="charge_summary"
              value={formState.charge_summary}
              onChange={handleChange}
              placeholder={t("chargeSummaryPlaceholder")}
              className="w-full border border-gray-300 bg-white rounded-lg px-4 py-3 text-sm focus:border-red-800 focus:ring-2 focus:ring-red-100 transition-colors resize-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <h3 className="text-sm font-semibold text-gray-700">
                  {t("articleOfCharge")}
                </h3>
              </div>
            </div>
            <textarea
              required
              rows={4}
              name="artcicle_of_charge"
              value={formState.artcicle_of_charge}
              onChange={handleChange}
              placeholder={t("articleOfChargePlaceholder")}
              className="w-full border border-gray-300 bg-white rounded-lg px-4 py-3 text-sm focus:border-red-800 focus:ring-2 focus:ring-red-100 transition-colors resize-none"
            />
          </div>
        </section>

        {/* Charge Document Upload */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="text-sm font-semibold text-gray-700">
                {t("chargeDocument")}
              </h3>
            </div>
          </div>

          <div
            onClick={triggerFileInput}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors group ${
              formState.files.length > 0
                ? "border-red-300 bg-red-50"
                : "border-gray-300 hover:border-red-800 hover:bg-red-50"
            }`}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <div
              className={`w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center transition-colors ${
                formState.files.length > 0
                  ? "bg-red-100"
                  : "bg-gray-100 group-hover:bg-red-100"
              }`}
            >
              <svg
                className={`w-6 h-6 ${formState.files.length > 0 ? "text-red-800" : "text-gray-500 group-hover:text-red-800"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>

            {formState.files.length > 0 ? (
              <>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  {formState.files[0].name}
                </p>
                <p className="text-xs text-gray-500">
                  {(formState.files[0].size / 1024 / 1024).toFixed(2)} MB •{" "}
                  {t("clickToChange")}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {t("uploadChargeDocument")}
                </p>
                <p className="text-xs text-gray-500">
                  {t("clickToUploadOrDrag")}
                </p>
                <p className="text-xs text-gray-400 mt-2">{t("fileTypes")}</p>
              </>
            )}
          </div>

          {formState.files.length > 0 && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={removeFile}
                className="text-xs text-red-800 hover:text-red-700 hover:underline flex items-center gap-1"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                {t("removeFile")}
              </button>
            </div>
          )}
        </section>

        {/* Action Buttons */}
        <div className="sticky bottom-0 pt-6 bg-white border-t border-gray-200">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitLoading}
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-red-800 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={
                isSubmitLoading ||
                !formState.charge_summary.trim() ||
                !formState.artcicle_of_charge.trim() ||
                formState.files.length === 0
              }
            >
              {isSubmitLoading ? (
                <div className="flex items-center justify-center gap-2">
                  {LoadingIcon}
                  <span>{t("framingCharges")}...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>{t("frameChargeDocument")}</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </form>
    </SideModal>
  );
}
